import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import { useState } from "react";
import { PREDEFINED_SCALES } from "../pages/teacher/GradeScalesManager";
import { getGrades } from "../../utils/grades";
import useUserStore from "../../stores/user";
import { useEffect } from "react";
import { useUiLang } from "../../stores/uiLang";
import { CLASS_FORM_T, termOptions, termLabel, currentTerm } from "../../utils/classForm";

interface CreateClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: {
    name: string;
    description?: string;
    semester: string;
    createJoinCode: boolean;
    settings: {
      class_time: string;
      reflection_requirements?: {
        min_words?: number;
        min_sentences?: number;
        enabled: boolean;
      };
    };
    grade_type: string;
  }) => Promise<{ error: string | null }>;
}

interface CreateClassFormData {
  name: string;
  description?: string;
  semester: string;
  createJoinCode: boolean;
  settings: {
    class_time: string;
    reflection_requirements?: {
      min_words?: number;
      min_sentences?: number;
      enabled: boolean;
    };
  };
  grade_type: string;
}

export default function CreateClassModal({
  isOpen,
  onClose,
  onCreate,
}: CreateClassModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lang = useUiLang();
  const t = CLASS_FORM_T[lang];

  const { user } = useUserStore();
  const [customScales, setCustomScales] = useState<any[]>([]);

  useEffect(() => {
    if (user?.id) {
      getGrades(user.id).then(({ grades }) => setCustomScales(grades));
    }
  }, [user?.id]);

  if (!isOpen) return null;

  const initialValues: {
    name: string;
    description: string;
    semester: string;
    class_time: string;
    createJoinCode: boolean;
    grade_type: string;
    reflection_requirements_enabled: boolean;
    reflection_min_words: number;
    reflection_min_sentences: number | null;
  } = {
    name: "",
    description: "",
    semester: currentTerm(),
    class_time: "",
    createJoinCode: true,
    grade_type: "Pass / Fail",
    reflection_requirements_enabled: false,
    reflection_min_words: 75,
    reflection_min_sentences: 2,
  };

  const gradeScaleOptions = [
    ...PREDEFINED_SCALES.map((scale) => ({
      id: scale.id,
      name: scale.name,
      isPredefined: true,
    })),
    ...customScales.map((scale) => ({
      id: scale.id,
      name: scale.name,
      isPredefined: false,
    })),
  ];

  const validationSchema = yup.object().shape({
    name: yup.string().required("Class name is required"),
    grade_type: yup
      .string()
      .oneOf(gradeScaleOptions.map((scale) => scale.name))
      .required("Grade type is required"),
    reflection_min_words: yup
      .number()
      .min(1, "Minimum words must be at least 1")
      .nullable()
      .when("reflection_requirements_enabled", {
        is: true,
        then: (schema) =>
          schema.required(
            "Minimum words is required when reflection is enabled"
          ),
      }),
    reflection_min_sentences: yup
      .number()
      .min(1, "Minimum sentences must be at least 1")
      .nullable()
      .when("reflection_requirements_enabled", {
        is: true,
        then: (schema) =>
          schema.required(
            "Minimum sentences is required when reflection is enabled"
          ),
      }),
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold">{t.createTitle}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSubmitting}
          >
            ✕
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, { resetForm }) => {
            try {
              setIsSubmitting(true);
              const result = await onCreate({
                name: values.name,
                description: values.description,
                semester: values.semester,
                createJoinCode: values.createJoinCode,
                settings: {
                  class_time: values.class_time,
                  // CT-6: the reflection word/sentence minimums are no longer offered.
                  reflection_requirements: {
                    enabled: false,
                  },
                },
                grade_type: values.grade_type, // now the name
              });

              if (!result.error) {
                resetForm();
                onClose();
              }
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          {({ values }) => (
            <Form className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t.className}
                </label>
                <Field
                  name="name"
                  className="w-full border p-2 rounded"
                  placeholder={t.classNamePlaceholder}
                />
                <ErrorMessage
                  name="name"
                  component="div"
                  className="text-sm text-red-600 mt-1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.classTime}
                  </label>
                  <Field
                    name="class_time"
                    placeholder={t.classTimePlaceholder}
                    className="w-full border p-2 rounded"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t.term}
                  </label>
                  <Field
                    as="select"
                    name="semester"
                    className="w-full border p-2 rounded"
                  >
                    {/* Run E2 J2 (D31): terms worked out from today's date */}
                    {termOptions(values.semester).map((term) => (
                      <option key={term} value={term}>
                        {termLabel(term, lang)}
                      </option>
                    ))}
                  </Field>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t.gradeType}
                </label>
                <Field
                  as="select"
                  name="grade_type"
                  className="w-full border p-2 rounded"
                >
                  {gradeScaleOptions.map((scale) => (
                    <option key={scale.id} value={scale.name}>
                      {scale.name}
                    </option>
                  ))}
                </Field>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t.description}
                </label>
                <Field
                  as="textarea"
                  name="description"
                  rows={3}
                  placeholder={t.descriptionPlaceholder}
                  className="w-full border p-2 rounded"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Field
                  type="checkbox"
                  name="createJoinCode"
                  className="h-4 w-4"
                />
                <label className="text-sm">{t.joinCode}</label>
              </div>
              <p className="text-xs text-gray-500">
                {t.joinCodeHelp}
              </p>
              <div className="flex justify-end space-x-2 pt-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className={`btn btn-small btn-secondary ${
                    isSubmitting ? "bg-gray-300 cursor-not-allowed" : ""
                  }`}
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`btn btn-small ${
                    isSubmitting ? "bg-indigo-300 cursor-not-allowed" : ""
                  }`}
                >
                  {isSubmitting ? t.creating : t.create}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
